/**
 * Quotes — list, plus the kit previewer.
 *
 * The previewer is the bit that matters on a phone: pick a kit, spin the
 * driver quantity, and every dependent line moves with it. It saves nothing,
 * so a tech can price three options in a car park without leaving drafts
 * behind.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import * as actions from "../lib/actions";
import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { useLocal } from "../lib/local";
import * as repo from "../lib/repo";
import { money, percent, qty, titleCase } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, type Tone } from "../components/ui";
import type { QuoteStatus } from "../lib/types";

const STATUS_TONE: Record<QuoteStatus, Tone> = {
  draft: "neutral",
  awaiting_approval: "warn",
  sent: "info",
  viewed: "info",
  accepted: "ok",
  declined: "neutral",
  expired: "neutral",
};

export default function Quotes() {
  const quotes = useQuery({ queryKey: ["quotes"], queryFn: () => api.quotes.list() });
  const { can } = useAuth();

  return (
    <div className="page">
      <PageHeader
        title="Quotes"
        actions={
          can(...OFFICE) && (
            <Link className="btn btn-primary" to="/quotes/new">
              New quote
            </Link>
          )
        }
      >
        Build quotes from reusable work packages or individual lines, with
        materials, labour, subcontractors and hire priced together.
      </PageHeader>

      <KitPreview />

      <Card title="Quotes" padded={false}>
        {quotes.isPending ? (
          <Loading what="Loading quotes" />
        ) : quotes.error ? (
          <ErrorState
            error={quotes.error}
            onRetry={() => void quotes.refetch()}
            needs="The quote list lives in the office."
          />
        ) : quotes.data.length === 0 ? (
          <Empty title="No quotes yet">
            Price a package above, then build a quote from it.
          </Empty>
        ) : (
          <DataTable
            columns={[
              { key: "no", label: "Quote" },
              { key: "title", label: "Work" },
              { key: "status", label: "Status" },
              { key: "margin", label: "Margin", align: "right", hideOnPhone: true },
              { key: "total", label: "Total", align: "right" },
            ]}
          >
            {quotes.data.map((quote) => (
              <tr key={quote.id} className="clickable">
                <td className="mono">
                  <Link className="row-link" to={`/quotes/${quote.id}`}>
                    {quote.number}
                  </Link>
                </td>
                <td>{quote.title}</td>
                <td>
                  <Chip tone={STATUS_TONE[quote.status]}>{titleCase(quote.status)}</Chip>
                </td>
                <td className="right mono phone-hide">{percent(quote.target_margin_bp)}</td>
                <td className="right mono">{money(quote.total_cents)}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </Card>
    </div>
  );
}

/**
 * The site-walk calculator, read from the device.
 *
 * This is the one screen where working offline is the whole feature: a tech
 * in a car park spins the camera count and watches the cable, the labour and
 * the EWP days move with it, in front of the customer. It reads the kits and
 * the catalogue out of the local store and expands them with `pricing.ts`, a
 * tested port of the server's own arithmetic (D23) — so the number quoted on
 * site is the number the office will see.
 */
function KitPreview() {
  const [kitId, setKitId] = useState<string>("");
  const [driver, setDriver] = useState(8);
  const [margin, setMargin] = useState(4200);

  const kits = useLocal(() => repo.kits(), []);
  const kitRows = kits.data ?? [];

  const selected = kitId || String(kitRows[0]?.id ?? "");
  const kitRow = kitRows.find((k) => String(k.id) === selected);
  const kit = kitRow
    ? {
        id: String(kitRow.id),
        name: String(kitRow.name ?? ""),
        driver_label: String(kitRow.driver_label ?? "quantity"),
      }
    : undefined;

  const preview = useLocal(
    () => (selected && driver > 0 ? repo.kitPreview(selected, driver, { targetMarginBp: margin }) : Promise.resolve(null)),
    [selected, driver, margin],
  );

  if (kits.isPending) return <Card title="Price a package"><Loading what="Loading packages" /></Card>;
  if (kitRows.length === 0) {
    return (
      <Card title="Price a package">
        <Empty title="No quote packages set up yet">
          An owner or office user can create reusable work packages under Catalogue.
          After saving, sync this device to carry them for offline quoting.
        </Empty>
      </Card>
    );
  }

  return (
    <Card title="Price a package" subtitle="Nothing is saved — this is the site-walk calculator">
      <div className="kit-controls">
        <label className="field">
          <span>Package</span>
          <select value={selected} onChange={(e) => setKitId(e.target.value)}>
            {kitRows.map((k) => (
              <option key={String(k.id)} value={String(k.id)}>
                {String(k.name)}
              </option>
            ))}
          </select>
        </label>

        <label className="field field-narrow">
          <span>{kit ? titleCase(kit.driver_label) : "Quantity"}</span>
          <input
            type="number"
            min={1}
            max={200}
            inputMode="numeric"
            value={driver}
            onChange={(e) => setDriver(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>

        <label className="field">
          <span>
            Target margin <b className="mono">{percent(margin, 0)}</b>
          </span>
          <input
            className="slider"
            type="range"
            min={1800}
            max={6200}
            step={100}
            value={margin}
            onChange={(e) => setMargin(Number(e.target.value))}
          />
        </label>
      </div>

      {preview.isPending ? (
        <Loading what="Pricing" />
      ) : !preview.data ? (
        <Empty title="That package isn't on this device">
          Sync when you're back in range and it'll be here for the next walk.
        </Empty>
      ) : (
        <>
          {preview.data.missing_items > 0 && (
            // Said out loud rather than swallowed: a kit line whose catalogue
            // item has not synced is left out, and a quote that is quietly
            // short two items is worse than one that says it is.
            <p className="notice notice-warn" role="status">
              {preview.data.missing_items} line
              {preview.data.missing_items > 1 ? "s aren't" : " isn't"} on this device yet, so
              {preview.data.missing_items > 1 ? " they are" : " it is"} not in this price. Sync to
              get the full package.
            </p>
          )}

          <div className="quote-totals">
            <div>
              <span className="tile-label">Cost</span>
              <b className="mono">{money(preview.data.totals.cost_cents)}</b>
            </div>
            <div>
              <span className="tile-label">Margin</span>
              <b className="mono text-accent">
                {money(preview.data.totals.subtotal_cents - preview.data.totals.cost_cents)}
              </b>
            </div>
            <div>
              <span className="tile-label">Total inc GST</span>
              <b className="mono big">{money(preview.data.totals.total_cents)}</b>
            </div>
          </div>

          <div className="table-scroll" style={{ marginTop: 14 }}>
            <table>
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Qty</th>
                  <th className="right phone-hide">Cost</th>
                  <th className="right">Charge</th>
                </tr>
              </thead>
              <tbody>
                {preview.data.lines.map((line, i) => (
                  <tr key={`${line.description}-${i}`}>
                    <td>{line.description}</td>
                    <td className="mono">
                      {qty(line.qty)} {line.uom}
                    </td>
                    <td className="right mono muted phone-hide">{money(line.cost_cents)}</td>
                    <td className="right mono">{money(line.sell_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="tiny muted" style={{ marginTop: 10 }}>
            {preview.data.lines.length} lines came with the package. The same expansion
            runs on the device, so these numbers hold with no signal.
          </p>

          <SaveQuote
            kitId={selected}
            kitName={kit?.name ?? "Quote"}
            driver={driver}
            margin={margin}
            total={preview.data.totals.total_cents}
          />
        </>
      )}
    </Card>
  );
}

/**
 * Turn the calculator into a quote.
 *
 * The site picker reads from the device, so a tech can do this standing in a
 * comms room. What comes back distinguishes a quote the server numbered from
 * one this phone did — "saved as Q-1042" and "saved on this phone as
 * Q-D4-0003" are different things to tell a customer, and the second one is
 * still worth saying because it is the number on the docket they were handed.
 */
function SaveQuote({
  kitId,
  kitName,
  driver,
  margin,
  total,
}: {
  kitId: string;
  kitName: string;
  driver: number;
  margin: number;
  total: number;
}) {
  const { data: sites } = useLocal(() => repo.sitePicker(), []);
  const [siteId, setSiteId] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ number: string; queued: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const options = sites ?? [];
  const chosen = options.find((s) => s.id === (siteId || options[0]?.id));

  async function save() {
    if (!chosen) return;
    setSaving(true);
    setError(null);
    try {
      const { result, queued } = await actions.saveQuote({
        customer_id: chosen.customer_id,
        site_id: chosen.id,
        title: kitName,
        target_margin_bp: margin,
        sections: [{ kit_id: kitId, title: kitName, driver_qty: driver }],
      });
      setSaved({ number: result.number, queued });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that quote.");
    } finally {
      setSaving(false);
    }
  }

  if (options.length === 0) return null;

  return (
    <div className="save-quote">
      {saved ? (
        <p className={`notice ${saved.queued ? "notice-info" : "notice-ok"}`} role="status">
          {saved.queued ? (
            <>
              Saved on this phone as <b className="mono">{saved.number}</b> — write that on the
              docket. It'll send when you're back in range and keep the same reference.
            </>
          ) : (
            <>
              Saved as <b className="mono">{saved.number}</b>.
            </>
          )}
        </p>
      ) : (
        <>
          {error && (
            <p className="notice notice-crit" role="alert">
              {error}
            </p>
          )}
          <div className="kit-controls">
            <label className="field">
              <span>Save against</span>
              <select value={siteId || options[0]?.id} onChange={(e) => setSiteId(e.target.value)}>
                {options.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
              {saving ? "Saving…" : `Save this quote — ${money(total)}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
